"use client";

import { useMemo, useState } from "react";
import { SearchIcon } from "lucide-react";

import { EventCard, type PublicEvent } from "@/components/events/EventCard";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { EVENT_CATEGORIES, type EventCategoryKey } from "@/lib/constants";

export function EventsExplorer({ events }: { events: PublicEvent[] }) {
  const [category, setCategory] = useState<EventCategoryKey | "ALL">("ALL");
  const [city, setCity] = useState("ALL");
  const [query, setQuery] = useState("");

  const cities = useMemo(
    () => Array.from(new Set(events.map((e) => e.city))).sort(),
    [events]
  );

  const filtered = events.filter((e) => {
    if (category !== "ALL" && e.category !== category) return false;
    if (city !== "ALL" && e.city !== city) return false;
    if (query && !`${e.title} ${e.organizationName}`.toLowerCase().includes(query.toLowerCase()))
      return false;
    return true;
  });

  return (
    <div id="eventos" className="scroll-mt-20">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={category === "ALL" ? "default" : "outline"}
            onClick={() => setCategory("ALL")}
            className={cn("rounded-full")}
          >
            Todos
          </Button>
          {(Object.keys(EVENT_CATEGORIES) as EventCategoryKey[]).map((key) => (
            <Button
              key={key}
              size="sm"
              variant={category === key ? "default" : "outline"}
              onClick={() => setCategory(key)}
              className="rounded-full"
            >
              {EVENT_CATEGORIES[key].label}
            </Button>
          ))}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Busca por título o por organización…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={city} onChange={(e) => setCity(e.target.value)} className="sm:w-48">
            <option value="ALL">Todas las ciudades</option>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-10 rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          No hay eventos que coincidan con tu búsqueda.
        </div>
      ) : (
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}
    </div>
  );
}
